import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Send, Building2 } from 'lucide-react';
import { integrationsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import config from '../config';

const BITRIX_ERROR_MESSAGES = {
  bitrix_denied: 'Подключение к Bitrix24 отменено',
  bitrix_invalid_request: 'Некорректный ответ от Bitrix24 — попробуйте снова',
  bitrix_invalid_state: 'Сессия подключения устарела — попробуйте снова',
  bitrix_not_configured: 'Сначала сохраните данные портала Bitrix24',
  bitrix_unavailable: 'Bitrix24 временно недоступен — попробуйте позже',
  bitrix_token_exchange_failed: 'Не удалось подтвердить подключение к Bitrix24',
};

const BITRIX_STATUS_BADGE = {
  not_configured: { className: 'bg-gray-200 text-gray-700', label: 'Не подключено' },
  pending_auth: { className: 'bg-amber-100 text-amber-800', label: 'Ожидает авторизации' },
  active: { className: 'bg-green-100 text-green-800', label: 'Подключено' },
};

export default function Connectors() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // --- Telegram ---
  const [editing, setEditing] = useState(false);
  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['telegram-connector'],
    queryFn: () => integrationsApi.getTelegram().then((res) => res.data),
  });

  const connected = data?.connected;

  const startEditing = () => {
    setBotToken('');
    setChatId(data?.chat_id || '');
    setEditing(true);
  };

  const handleSave = async () => {
    if (!botToken || !chatId) {
      toast.error('Заполните Bot Token и Chat ID');
      return;
    }
    setSaving(true);
    try {
      await integrationsApi.saveTelegram({ bot_token: botToken, chat_id: chatId });
      queryClient.invalidateQueries({ queryKey: ['telegram-connector'] });
      toast.success('Telegram подключён — тестовое сообщение отправлено в чат');
      setEditing(false);
      setBotToken('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось подключить Telegram');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await integrationsApi.testTelegram();
      toast.success('Тестовое сообщение отправлено');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Проверка не удалась');
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await integrationsApi.deleteTelegram();
      queryClient.invalidateQueries({ queryKey: ['telegram-connector'] });
      toast.success('Telegram отключён');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось отключить Telegram');
    }
  };

  // --- Bitrix24 ---
  const [bxEditing, setBxEditing] = useState(false);
  const [portalDomain, setPortalDomain] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [bxChatId, setBxChatId] = useState('');
  const [bxSaving, setBxSaving] = useState(false);
  const [bxTesting, setBxTesting] = useState(false);
  const [bxConnecting, setBxConnecting] = useState(false);

  const { data: bxData, isLoading: bxLoading } = useQuery({
    queryKey: ['bitrix-connector'],
    queryFn: () => integrationsApi.getBitrix().then((res) => res.data),
  });

  React.useEffect(() => {
    const error = searchParams.get('error');
    const bitrixConnected = searchParams.get('bitrix');
    if (error && error.startsWith('bitrix_')) {
      toast.error(BITRIX_ERROR_MESSAGES[error] || 'Ошибка подключения к Bitrix24');
      setSearchParams({}, { replace: true });
    } else if (bitrixConnected === 'connected') {
      toast.success('Bitrix24 подключён');
      queryClient.invalidateQueries({ queryKey: ['bitrix-connector'] });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const startBxEditing = () => {
    setPortalDomain(bxData?.portal_domain || '');
    setClientId(bxData?.client_id || '');
    setClientSecret('');
    setBxChatId(bxData?.chat_id || '');
    setBxEditing(true);
  };

  const handleBxSave = async () => {
    if (!portalDomain || !clientId || !clientSecret || !bxChatId) {
      toast.error('Заполните все поля');
      return;
    }
    setBxSaving(true);
    try {
      const res = await integrationsApi.saveBitrix({
        portal_domain: portalDomain,
        client_id: clientId,
        client_secret: clientSecret,
        chat_id: bxChatId,
      });
      queryClient.invalidateQueries({ queryKey: ['bitrix-connector'] });
      if (res.data.status === 'active') {
        toast.success('Данные Bitrix24 сохранены');
      } else if (res.data.reauth_required) {
        toast.success('Данные портала изменены — переподключите Bitrix24');
      } else {
        toast.success('Сохранено — нажмите «Подключить через Bitrix24», чтобы завершить подключение');
      }
      setBxEditing(false);
      setClientSecret('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось сохранить данные Bitrix24');
    } finally {
      setBxSaving(false);
    }
  };

  const handleBxConnect = async () => {
    setBxConnecting(true);
    try {
      const res = await integrationsApi.getBitrixConnectUrl();
      window.location.href = res.data.authorize_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось начать подключение к Bitrix24');
      setBxConnecting(false);
    }
  };

  const handleBxTest = async () => {
    setBxTesting(true);
    try {
      await integrationsApi.testBitrix();
      toast.success('Тестовое сообщение отправлено');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Проверка не удалась');
    } finally {
      setBxTesting(false);
    }
  };

  const handleBxDisconnect = async () => {
    try {
      await integrationsApi.deleteBitrix();
      queryClient.invalidateQueries({ queryKey: ['bitrix-connector'] });
      toast.success('Bitrix24 отключён');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось отключить Bitrix24');
    }
  };

  const bxStatus = bxData?.status || 'not_configured';
  const bxBadge = BITRIX_STATUS_BADGE[bxStatus];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Коннекторы</h1>
        <p className="text-gray-600">Подключите свои каналы для получения отчётов и уведомлений</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-600" />
              <CardTitle>Telegram</CardTitle>
            </div>
            {!isLoading && (
              <Badge className={connected ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}>
                {connected ? 'Подключено' : 'Не подключено'}
              </Badge>
            )}
          </div>
          <CardDescription>
            Отчёты и уведомления (ежедневный отчёт, просроченные платежи) будут приходить в этот бот и чат.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <p className="text-sm text-gray-500">Загрузка…</p>
          ) : editing ? (
            <div className="space-y-3 max-w-md">
              <div className="text-sm text-gray-600 bg-gray-50 rounded-md p-3 space-y-1">
                <p>1. Создайте бота через <span className="font-medium">@BotFather</span> в Telegram и скопируйте токен.</p>
                <p>2. Добавьте бота в нужный чат или группу.</p>
                <p>3. Узнайте Chat ID (например, через бота @userinfobot или @getmyid_bot) и укажите его ниже.</p>
              </div>
              <div>
                <Label htmlFor="bot-token">Bot Token</Label>
                <Input
                  id="bot-token"
                  value={botToken}
                  onChange={(e) => setBotToken(e.target.value)}
                  placeholder="123456789:AA..."
                  data-testid="telegram-bot-token-input"
                />
              </div>
              <div>
                <Label htmlFor="chat-id">Chat ID</Label>
                <Input
                  id="chat-id"
                  value={chatId}
                  onChange={(e) => setChatId(e.target.value)}
                  placeholder="-1001234567890"
                  data-testid="telegram-chat-id-input"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setEditing(false)} disabled={saving}>
                  Отмена
                </Button>
                <Button onClick={handleSave} disabled={saving} data-testid="save-telegram-connector-button">
                  {saving ? 'Проверяем и сохраняем…' : 'Сохранить и проверить'}
                </Button>
              </div>
            </div>
          ) : connected ? (
            <div className="space-y-3">
              <div className="text-sm text-gray-600 space-y-1">
                <p>Bot Token: <span className="font-mono">{data.bot_token_masked}</span></p>
                <p>Chat ID: <span className="font-mono">{data.chat_id}</span></p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="test-telegram-connector-button">
                  {testing ? 'Проверяем…' : 'Проверить'}
                </Button>
                <Button variant="outline" onClick={startEditing}>
                  Изменить
                </Button>
                <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={handleDisconnect} data-testid="disconnect-telegram-connector-button">
                  Отключить
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-500 mb-3">Telegram ещё не подключён — отчёты недоступны, пока не настроите канал.</p>
              <Button onClick={startEditing} data-testid="connect-telegram-connector-button">
                Подключить Telegram
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              <CardTitle>Bitrix24</CardTitle>
            </div>
            {!bxLoading && (
              <Badge className={bxBadge.className}>{bxBadge.label}</Badge>
            )}
          </div>
          <CardDescription>
            Отчёты будут приходить в групповой чат вашего портала Bitrix24.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {bxLoading ? (
            <p className="text-sm text-gray-500">Загрузка…</p>
          ) : bxEditing ? (
            <div className="space-y-3 max-w-md">
              <div className="text-sm text-gray-600 bg-gray-50 rounded-md p-3 space-y-1">
                <p>1. В Bitrix24: «Ещё» → «Разработчикам» → «Другое» → «Локальное приложение» — создайте приложение с правом <span className="font-medium">im</span>.</p>
                <p>2. В поле Redirect URI укажите: <span className="font-mono text-xs break-all">{config.API_BASE}/bitrix/oauth/callback</span></p>
                <p>3. Скопируйте домен портала, client_id и client_secret созданного приложения.</p>
                <p>4. ID группового чата — откройте нужный чат в Bitrix24, в адресе страницы будет вида <span className="font-mono text-xs">chat123</span> — это и есть ID.</p>
              </div>
              <div>
                <Label htmlFor="bx-portal-domain">Домен портала</Label>
                <Input
                  id="bx-portal-domain"
                  value={portalDomain}
                  onChange={(e) => setPortalDomain(e.target.value)}
                  placeholder="datametrics.bitrix24.ru"
                  data-testid="bitrix-portal-domain-input"
                />
              </div>
              <div>
                <Label htmlFor="bx-client-id">Client ID</Label>
                <Input
                  id="bx-client-id"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="local.xxxxxxxxxxxxx"
                  data-testid="bitrix-client-id-input"
                />
              </div>
              <div>
                <Label htmlFor="bx-client-secret">Client Secret</Label>
                <Input
                  id="bx-client-secret"
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder="••••••••"
                  data-testid="bitrix-client-secret-input"
                />
              </div>
              <div>
                <Label htmlFor="bx-chat-id">Chat ID</Label>
                <Input
                  id="bx-chat-id"
                  value={bxChatId}
                  onChange={(e) => setBxChatId(e.target.value)}
                  placeholder="chat3464"
                  data-testid="bitrix-chat-id-input"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setBxEditing(false)} disabled={bxSaving}>
                  Отмена
                </Button>
                <Button onClick={handleBxSave} disabled={bxSaving} data-testid="save-bitrix-connector-button">
                  {bxSaving ? 'Сохраняем…' : 'Сохранить'}
                </Button>
              </div>
            </div>
          ) : bxStatus === 'not_configured' ? (
            <div>
              <p className="text-sm text-gray-500 mb-3">Bitrix24 ещё не подключён — отчёты недоступны, пока не настроите канал.</p>
              <Button onClick={startBxEditing} data-testid="connect-bitrix-connector-button">
                Подключить Bitrix24
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-gray-600 space-y-1">
                <p>Портал: <span className="font-mono">{bxData.portal_domain}</span></p>
                <p>Client ID: <span className="font-mono">{bxData.client_id}</span></p>
                <p>Client Secret: <span className="font-mono">{bxData.client_secret_masked}</span></p>
                <p>Chat ID: <span className="font-mono">{bxData.chat_id}</span></p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {bxStatus === 'active' && (
                  <Button variant="outline" onClick={handleBxTest} disabled={bxTesting} data-testid="test-bitrix-connector-button">
                    {bxTesting ? 'Проверяем…' : 'Проверить'}
                  </Button>
                )}
                <Button variant="outline" onClick={handleBxConnect} disabled={bxConnecting} data-testid="connect-bitrix-oauth-button">
                  {bxConnecting ? 'Переходим…' : bxStatus === 'active' ? 'Переподключить' : 'Подключить через Bitrix24'}
                </Button>
                <Button variant="outline" onClick={startBxEditing}>
                  Изменить
                </Button>
                <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={handleBxDisconnect} data-testid="disconnect-bitrix-connector-button">
                  Отключить
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
